import { query } from "../../erd-editor-schema/dist/query/index.js";
import { Direction } from "../../constants/schema.js";
import { arrayHas } from "../arrayHas.js";
import { DirectionName, DirectionNameList, copyAnchor, heldRouteRedraws, nextSortEpoch, setAnchors, setRoute, setStubSlots } from "./index.js";
import { getVisibleIds } from "../../konva/scene/viewLayout.js";
import { euclideanDistance, tableToObjectPoint } from "./calc.js";
import { createDirtyLanes, diffTableBoxes, getSortCache, markRoute, routeTouches } from "./incremental.js";
import { stubEnds } from "./stub.js";
import { collectObstacles, routeWithReach } from "./route.js";
import { nudgeRoutes } from "./nudge.js";
//#region src/utils/draw-relationship/sort.ts
var directionNameToDirection = {
	[DirectionName.top]: Direction.top,
	[DirectionName.bottom]: Direction.bottom,
	[DirectionName.left]: Direction.left,
	[DirectionName.right]: Direction.right
};
var TAU = Math.PI * 2;
var SELF_CORNER_OFFSET = 20;
var SELF_CORNER_STRIDE = 18;
var SELF_CLEARANCE = 8;
var SQUEEZED_PITCH = 12;
var SIDE_START_ANGLE = {
	[DirectionName.top]: -Math.PI,
	[DirectionName.right]: -Math.PI / 2,
	[DirectionName.bottom]: 0,
	[DirectionName.left]: Math.PI / 2
};
var WALKS_BACKWARDS = {
	[DirectionName.top]: false,
	[DirectionName.right]: false,
	[DirectionName.bottom]: true,
	[DirectionName.left]: true
};
function relationshipSort(state, source = "document", { route = true } = {}) {
	nextSortEpoch(source);
	const { tableIds, relationshipIds } = getVisibleIds(state, source);
	const { collections } = state;
	const isTableIds = arrayHas(tableIds);
	const tableCollection = query(collections).collection("tableEntities");
	const relationships = query(collections).collection("relationshipEntities").selectByIds(relationshipIds).filter(({ start, end }) => isTableIds(start.tableId) && isTableIds(end.tableId));
	const graphMap = /* @__PURE__ */ new Map();
	const changeMap = /* @__PURE__ */ new Map();
	const slotMap = /* @__PURE__ */ new Map();
	const pairs = pairIndexes(relationships);
	for (const relationship of relationships) {
		const relationshipShape = createChangeRelationship(relationship);
		const { start, end } = relationshipShape;
		const startTable = tableCollection.selectById(start.tableId);
		const endTable = tableCollection.selectById(end.tableId);
		if (!startTable || !endTable) continue;
		changeMap.set(relationship, relationshipShape);
		slotMap.set(relationshipShape.id, [0, 0]);
		if (start.tableId === end.tableId) getOrCreateGraph(state, graphMap, startTable, source).loops.push(relationshipShape);
		else {
			const startGraph = getOrCreateGraph(state, graphMap, startTable, source);
			const endGraph = getOrCreateGraph(state, graphMap, endTable, source);
			const [startDirection, endDirection] = getAndSetDirection(startGraph.objectPoint, endGraph.objectPoint, relationshipShape, source);
			startGraph[startDirection].push({
				id: relationshipShape.id,
				isStart: true,
				point: start,
				angle: orderAngle(startDirection, startGraph, endGraph),
				tie: pairs.tieAt(relationshipShape.id, start.tableId, end.tableId)
			});
			endGraph[endDirection].push({
				id: relationshipShape.id,
				isStart: false,
				point: end,
				angle: orderAngle(endDirection, endGraph, startGraph),
				tie: pairs.tieAt(relationshipShape.id, end.tableId, start.tableId)
			});
		}
	}
	for (const graph of graphMap.values()) {
		placeLoops(graph);
		for (const key of DirectionNameList) placeSide(key, graph, slotMap);
	}
	for (const [origin, change] of changeMap.entries()) {
		setStubSlots(origin, slotMap.get(change.id) ?? [0, 0], source);
		setAnchors(origin, change, source);
	}
	if (source === "document") {
		if (route) routeRelationships(state, changeMap, slotMap);
	} else stubRelationships(changeMap, slotMap, source);
}
function stubRelationships(changeMap, slotMap, source) {
	for (const [origin, change] of changeMap.entries()) {
		const { id, start, end } = change;
		if (start.tableId === end.tableId) continue;
		const { m, l } = stubEnds(change, slotMap.get(id) ?? [0, 0]);
		setRoute(origin, [m, l], source);
	}
}
function routeRelationships(state, changeMap, slotMap) {
	const obstacles = collectObstacles(state);
	const cache = getSortCache(state);
	const moved = diffTableBoxes(cache, obstacles);
	const previous = cache.entries;
	const entries = /* @__PURE__ */ new Map();
	const dirty = /* @__PURE__ */ new Set();
	const lanes = createDirtyLanes();
	if (moved) for (const box of moved) lanes.markBox(box);
	const routes = /* @__PURE__ */ new Map();
	const endpoints = /* @__PURE__ */ new Map();
	const origins = /* @__PURE__ */ new Map();
	for (const [origin, change] of changeMap.entries()) {
		const { id, start, end } = change;
		if (start.tableId === end.tableId) continue;
		const { m, l } = stubEnds(change, slotMap.get(id) ?? [0, 0]);
		const was = previous.get(id);
		let pristine;
		let points;
		let bounds;
		if (moved !== null && was !== void 0 && sameRouteInputs(was, m, l, start, end) && !routeTouches(was.bounds, moved)) {
			pristine = was.pristine;
			points = clonePoints(pristine);
			bounds = was.bounds;
		} else {
			const routed = routeWithReach(m, start.direction, l, end.direction, obstacles, start.tableId, end.tableId);
			points = routed.points;
			bounds = routed.reach;
			pristine = clonePoints(points);
			dirty.add(id);
			if (was) markRoute(lanes, was.nudged);
			markRoute(lanes, pristine);
		}
		routes.set(id, points);
		endpoints.set(id, [start.tableId, end.tableId]);
		origins.set(id, origin);
		entries.set(id, {
			mx: m.x,
			my: m.y,
			mDirection: start.direction,
			lx: l.x,
			ly: l.y,
			lDirection: end.direction,
			startTableId: start.tableId,
			endTableId: end.tableId,
			pristine,
			nudged: points,
			bounds
		});
	}
	if (moved) {
		for (const [id, entry] of previous) if (!entries.has(id)) markRoute(lanes, entry.nudged);
	}
	nudgeRoutes(routes, obstacles, endpoints, moved === null ? void 0 : createMemo(previous, dirty, lanes));
	const held = heldRouteRedraws(state);
	for (const [id, points] of routes) {
		const origin = origins.get(id);
		if (origin) setRoute(origin, points, "document", held);
	}
	cache.entries = entries;
}
function clonePoints(points) {
	return points.map(({ x, y }) => ({
		x,
		y
	}));
}
function createMemo(previous, dirty, lanes) {
	return {
		dirty,
		lanes,
		coordinate(relationshipId, index, vertical) {
			const point = previous.get(relationshipId)?.nudged[index];
			if (!point) return void 0;
			return vertical ? point.x : point.y;
		}
	};
}
function sameRouteInputs(was, m, l, start, end) {
	return was.mx === m.x && was.my === m.y && was.lx === l.x && was.ly === l.y && was.mDirection === start.direction && was.lDirection === end.direction && was.startTableId === start.tableId && was.endTableId === end.tableId;
}
function getOrCreateGraph(state, graphMap, table, source) {
	let graph = graphMap.get(table.id);
	if (!graph) {
		const objectPoint = tableToObjectPoint(state, table, source);
		graph = {
			tableId: table.id,
			centerX: objectPoint.top.x,
			centerY: objectPoint.left.y,
			objectPoint,
			loops: [],
			top: [],
			bottom: [],
			left: [],
			right: []
		};
		graphMap.set(table.id, graph);
	}
	return graph;
}
function placeLoops(graph) {
	const { rt } = graph.objectPoint;
	const offsets = loopOffsets(graph);
	graph.loops.forEach((relationship, index) => {
		const offset = offsets[index];
		relationship.start.direction = Direction.top;
		relationship.start.x = rt.x - offset;
		relationship.start.y = rt.y;
		relationship.end.direction = Direction.right;
		relationship.end.x = rt.x;
		relationship.end.y = rt.y + offset;
	});
}
function loopOffsets(graph) {
	const { width, height } = graph.objectPoint;
	const count = graph.loops.length;
	const room = Math.max(0, Math.min(width, height) - 6);
	const first = Math.min(SELF_CORNER_OFFSET, room);
	const stride = count > 1 ? Math.min(SELF_CORNER_STRIDE, (room - first) / (count - 1)) : 0;
	return graph.loops.map((_, index) => first + index * stride);
}
function selfReserve(graph) {
	const offsets = loopOffsets(graph);
	if (!offsets.length) return 0;
	return offsets[offsets.length - 1] + SELF_CLEARANCE;
}
function createChangeRelationship({ id, start, end }) {
	return {
		id,
		start: copyAnchor(start),
		end: copyAnchor(end)
	};
}
function pairIndexes(relationships) {
	const groups = /* @__PURE__ */ new Map();
	for (const { id, start, end } of relationships) {
		const key = pairKey(start.tableId, end.tableId);
		const group = groups.get(key);
		if (group) group.push(id);
		else groups.set(key, [id]);
	}
	const indexById = /* @__PURE__ */ new Map();
	const sizeById = /* @__PURE__ */ new Map();
	let anyShared = false;
	for (const group of groups.values()) {
		if (group.length < 2) continue;
		anyShared = true;
		group.sort();
		group.forEach((id, index) => {
			indexById.set(id, index);
			sizeById.set(id, group.length);
		});
	}
	return { tieAt(id, ownTableId, otherTableId) {
		if (!anyShared) return 0;
		const index = indexById.get(id) ?? 0;
		if (ownTableId <= otherTableId) return index;
		return (sizeById.get(id) ?? 1) - 1 - index;
	} };
}
function pairKey(a, b) {
	return a < b ? `${a}|${b}` : `${b}|${a}`;
}
function normalizeAngle(angle) {
	const value = angle % TAU;
	return value < 0 ? value + TAU : value;
}
function orderAngle(direction, own, other) {
	return normalizeAngle(Math.atan2(other.centerY - own.centerY, other.centerX - own.centerX) - SIDE_START_ANGLE[direction]);
}
var OUTWARD = {
	[DirectionName.left]: {
		x: -1,
		y: 0
	},
	[DirectionName.right]: {
		x: 1,
		y: 0
	},
	[DirectionName.top]: {
		x: 0,
		y: -1
	},
	[DirectionName.bottom]: {
		x: 0,
		y: 1
	}
};
function stubsFace(start, startSide, end, endSide) {
	if (startSide === endSide) return false;
	const a = OUTWARD[startSide];
	const b = OUTWARD[endSide];
	const mx = start[startSide].x + a.x * 24;
	const my = start[startSide].y + a.y * 24;
	const lx = end[endSide].x + b.x * 24;
	const ly = end[endSide].y + b.y * 24;
	return (lx - mx) * a.x + (ly - my) * a.y >= 0 && (mx - lx) * b.x + (my - ly) * b.y >= 0;
}
function nearestSides(start, end, accept) {
	let nearest = accept ? null : [DirectionName.bottom, DirectionName.bottom];
	let min = accept ? Infinity : euclideanDistance(start.bottom, end.bottom);
	for (const key of DirectionNameList) for (const key2 of DirectionNameList) {
		const k = key;
		const k2 = key2;
		const distance = euclideanDistance(start[k], end[k2]);
		if (min <= distance) continue;
		if (accept && !accept(k, k2)) continue;
		min = distance;
		nearest = [k, k2];
	}
	return nearest;
}
function getAndSetDirection(start, end, relationship, source) {
	let direction = nearestSides(start, end);
	const [startSide, endSide] = direction;
	if (source === "document" && !stubsFace(start, startSide, end, endSide)) direction = nearestSides(start, end, (k, k2) => stubsFace(start, k, end, k2)) ?? direction;
	const [k, k2] = direction;
	relationship.start.x = start[k].x;
	relationship.start.y = start[k].y;
	relationship.start.direction = directionNameToDirection[k];
	relationship.end.x = end[k2].x;
	relationship.end.y = end[k2].y;
	relationship.end.direction = directionNameToDirection[k2];
	return direction;
}
function placeSide(direction, graph, slotMap) {
	const entries = graph[direction];
	if (!entries.length) return;
	entries.sort((a, b) => a.angle - b.angle || a.tie - b.tie || (a.id < b.id ? -1 : 1));
	const { objectPoint } = graph;
	const isX = direction === DirectionName.top || direction === DirectionName.bottom;
	let low = (isX ? objectPoint.lt.x : objectPoint.lt.y) + 12;
	let high = (isX ? objectPoint.rb.x : objectPoint.rb.y) - 12;
	const reserve = selfReserve(graph);
	if (reserve) {
		if (direction === DirectionName.top) high -= reserve;
		else if (direction === DirectionName.right) low += reserve;
	}
	const needed = (entries.length - 1) * SQUEEZED_PITCH;
	if (reserve && high - low < needed) {
		const edgeLow = isX ? objectPoint.lt.x : objectPoint.lt.y;
		const edgeHigh = isX ? objectPoint.rb.x : objectPoint.rb.y;
		if (direction === DirectionName.right) {
			low = Math.min(edgeLow + reserve, edgeHigh);
			if (high - low < needed) high = edgeHigh;
		} else if (direction === DirectionName.top) {
			high = Math.max(edgeHigh - reserve, edgeLow);
			if (high - low < needed) low = edgeLow;
		}
	}
	if (high < low) {
		const middle = (low + high) / 2;
		low = middle;
		high = middle;
	}
	const available = high - low;
	const center = (low + high) / 2;
	const count = entries.length;
	const pitch = count > 1 ? Math.min(available / (count - 1), 120) : 0;
	const first = center - pitch * (count - 1) / 2;
	entries.forEach((entry, index) => {
		const step = WALKS_BACKWARDS[direction] ? count - 1 - index : index;
		const position = first + step * pitch;
		if (isX) {
			entry.point.x = position;
			entry.point.y = direction === DirectionName.top ? objectPoint.top.y : objectPoint.bottom.y;
		} else {
			entry.point.y = position;
			entry.point.x = direction === DirectionName.left ? objectPoint.left.x : objectPoint.right.x;
		}
		const slots = slotMap.get(entry.id);
		if (slots) slots[entry.isStart ? 0 : 1] = index;
	});
}
//#endregion
export { relationshipSort };
